import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import 'echarts-wordcloud'
import {
    Tabs, Upload, Button, Progress, message,
    Table, Tag, InputNumber, Badge,
} from 'antd'
import {
    InboxOutlined, CloudUploadOutlined, FileTextOutlined,
    FilterOutlined, CloseCircleOutlined, ApartmentOutlined,
    KeyOutlined, PieChartOutlined, DownloadOutlined,
} from '@ant-design/icons'
import * as echarts from 'echarts'
import {
    analyzeFile, analyzeText,
    analyzeNetwork, subscribeStream,
    fetchKeywordsFile, fetchKeywordsText,
} from '../../api/wordcloud'
import * as XLSX from 'xlsx'
import LoginModal from '../../components/LoginModal'
import WordcloudVipModal from '../../components/WordcloudVipModal'
import { setVipRequiredHandler } from '../../utils/errorHandler'
import styles from './index.module.css'

const { Dragger } = Upload
const { TextArea } = require('antd').Input

const COLORS = [
    '#1677ff', '#13c2c2', '#52c41a', '#fa8c16', '#eb2f96',
    '#722ed1', '#2f54eb', '#08979c', '#389e0d', '#d46b08',
    '#c41d7f', '#531dab', '#096dd9', '#006d75', '#237804',
    '#ad4e00', '#9e1068', '#391085',
]

const ChineseSegPage = () => {
    const [inputMode, setInputMode] = useState('file')
    const [fileList, setFileList] = useState([])
    const [textContent, setTextContent] = useState('')
    const [topN, setTopN] = useState(50)

    const [analyzing, setAnalyzing] = useState(false)
    const [phase, setPhase] = useState(null) // 'analyzing' | 'network'
    const [progress, setProgress] = useState({ current: 0, total: 0, message: '' })

    const [keywords, setKeywords] = useState([])      // [{word, score}]
    const [segmentResults, setSegmentResults] = useState([]) // [[word, count, pos], ...]
    const [networkData, setNetworkData] = useState(null)
    const [statsData, setStatsData] = useState(null)

    // Keep original input ref for the network phase
    const fileRef = useRef(null)
    const textRef = useRef('')

    const graphRef = useRef(null)
    const graphInstance = useRef(null)
    const pieRef = useRef(null)
    const pieInstance = useRef(null)
    const kwCloudRef = useRef(null)
    const kwCloudInstance = useRef(null)
    const [loginModalOpen, setLoginModalOpen] = useState(false)
    const [vipModalOpen, setVipModalOpen] = useState(false)

    const [messageApi, contextHolder] = message.useMessage()

    const isLoggedIn = !!localStorage.getItem('token')

    // 设置需要开通会员（err_code 8012）处理器
    useEffect(() => {
        const handler = () => setVipModalOpen(true)
        setVipRequiredHandler(handler)
        return () => setVipRequiredHandler(null)
    }, [])

    const handleVipSuccess = useCallback(() => {
        setVipModalOpen(false)
    }, [])

    /* ── ECharts force-directed graph ── */
    const renderGraph = useCallback((data) => {
        if (!graphRef.current) return
        if (graphInstance.current) {
            graphInstance.current.dispose()
            graphInstance.current = null
        }
        graphInstance.current = echarts.init(graphRef.current)

        const rawNodes = data.nodes || data.vertices || []
        const rawEdges = data.edges || data.links || []

        // 过滤掉 weight < 2 的边
        const filteredEdges = rawEdges.filter(e => {
            const w = e.weight != null ? e.weight : (e.value != null ? e.value : 0)
            return w >= 2
        })

        // 只保留在边中出现过的节点
        const activeWords = new Set()
        filteredEdges.forEach(e => { activeWords.add(e.source); activeWords.add(e.target) })
        const filteredNodes = rawNodes.filter(n => {
            const name = n.name || n.id || n.word || ''
            return activeWords.has(name)
        })

        // ── 计算每个节点的"加权度"（所有相连边的权重和），
        //    高加权度的节点放最内层，使粗边自然集中在中心 ──
        const weightSum = new Map()
        filteredEdges.forEach(e => {
            const w = e.value != null ? e.value : (e.weight != null ? e.weight : 0)
            weightSum.set(e.source, (weightSum.get(e.source) || 0) + w)
            weightSum.set(e.target, (weightSum.get(e.target) || 0) + w)
        })

        // 按 加权度 排序（同时保留 score 作为显示值）
        const scoredNodes = filteredNodes.map((n, i) => {
            const name = n.name || n.id || n.word || String(i)
            const score = Number(n.score != null ? n.score : (n.value != null ? n.value : (n.weight != null ? n.weight : 0)))
            const wDeg = weightSum.get(name) || 0
            return { raw: n, name, score, wDeg }
        }).sort((a, b) => b.wDeg - a.wDeg)

        const total = scoredNodes.length
        // 分层比例：内层 ~1/6，中层 ~1/3，外层 ~1/2
        const innerCount = Math.max(1, Math.round(total * 0.16))
        const middleCount = Math.max(1, Math.round(total * 0.34))
        const outerCount = total - innerCount - middleCount

        // 画布坐标系（ECharts 在 layout:'none' 时使用数据坐标）
        const RING_RADII = [120, 260, 420]
        const RING_NODE_SIZE = [22, 14, 9]
        const RING_FONT_SIZE = [15, 12, 10]
        const RING_COLOR_BORDER = ['#b91c1c', '#f59e0b', '#94a3b8']
        const tierIndexOf = (i) => {
            if (i < innerCount) return 0
            if (i < innerCount + middleCount) return 1
            return 2
        }

        // 节点名 -> 所属层级
        const tierMap = new Map()
        scoredNodes.forEach((s, i) => tierMap.set(s.name, tierIndexOf(i)))

        // 计算每层节点坐标（均匀分布在圆上）
        const placeOnRing = (count, radius, phaseOffset = 0) => {
            const pts = []
            for (let j = 0; j < count; j++) {
                const theta = (j / count) * Math.PI * 2 + phaseOffset
                pts.push({ x: Math.cos(theta) * radius, y: Math.sin(theta) * radius })
            }
            return pts
        }
        const innerPts = placeOnRing(innerCount, RING_RADII[0], -Math.PI / 2)
        const middlePts = placeOnRing(middleCount, RING_RADII[1], -Math.PI / 2 + Math.PI / middleCount)
        const outerPts = placeOnRing(outerCount, RING_RADII[2], -Math.PI / 2)

        const nodes = scoredNodes.map((s, i) => {
            const tier = tierIndexOf(i)
            const idxInTier = tier === 0 ? i : tier === 1 ? i - innerCount : i - innerCount - middleCount
            const pos = tier === 0 ? innerPts[idxInTier] : tier === 1 ? middlePts[idxInTier] : outerPts[idxInTier]
            return {
                name: s.name,
                value: s.score,
                x: pos.x,
                y: pos.y,
                symbolSize: RING_NODE_SIZE[tier],
                itemStyle: {
                    color: COLORS[i % COLORS.length],
                    borderColor: RING_COLOR_BORDER[tier],
                    borderWidth: tier === 0 ? 2 : 1,
                    shadowBlur: tier === 0 ? 12 : 6,
                    shadowColor: tier === 0 ? 'rgba(185,28,28,0.35)' : 'rgba(0,0,0,0.10)',
                },
                label: {
                    show: true,
                    fontSize: RING_FONT_SIZE[tier],
                    color: tier === 0 ? '#7f1d1d' : '#222',
                    fontWeight: tier === 0 ? 'bold' : 'normal',
                    borderRadius: 4,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    padding: [2, 6],
                },
            }
        })

        // 边权重分级，颜色渐变
        const edgeVals = filteredEdges.map(e => e.value != null ? e.value : (e.weight != null ? e.weight : 0.5))
        const maxEdge = Math.max(...edgeVals)
        const minEdge = Math.min(...edgeVals)

        function edgeColor(val, tier) {
            const t = (val - minEdge) / (maxEdge - minEdge + 1e-8)
            // 内层边用深红，外层边加深以提高可见度
            if (tier === 0) return t > 0.5 ? '#7f1d1d' : '#b91c1c'
            if (tier === 1) return t > 0.5 ? '#7f1d1d' : '#b91c1c'
            return t > 0.5 ? '#991b1b' : '#475569'
        }

        const links = filteredEdges.map((e) => {
            const w = e.value != null ? e.value : (e.weight != null ? e.weight : 0.5)
            const norm = maxEdge > minEdge ? (w - minEdge) / (maxEdge - minEdge) : 1
            // 边的层级取两端节点中"更外层"的那个：
            //   只有两端都在内层时 edgeTier=0（粗），
            //   只要有一端在外层就归到外层（细），
            //   这样粗边只出现在中心，不会从中心横穿到外圈。
            const ts = tierMap.get(e.source) ?? 2
            const tt = tierMap.get(e.target) ?? 2
            const edgeTier = Math.max(ts, tt)
            // 内层边显著加粗
            const baseWidth = edgeTier === 0 ? 3 : edgeTier === 1 ? 1 : 0.4
            const widthScale = edgeTier === 0 ? 5 : edgeTier === 1 ? 1.8 : 0.8
            // 提高细边的不透明度，让它们更清晰可见（粗细保持不变）
            const opacity = edgeTier === 0 ? 0.85 + norm * 0.15 : edgeTier === 1 ? 0.7 + norm * 0.25 : 0.55 + norm * 0.3
            return {
                source: e.source,
                target: e.target,
                value: w,
                lineStyle: {
                    width: baseWidth + norm * widthScale,
                    opacity,
                    color: edgeColor(w, edgeTier),
                    curveness: 0.12,
                },
                symbol: ['none', 'none'],
            }
        })

        graphInstance.current.setOption({
            backgroundColor: '#fafbff',
            graphic: [{
                type: 'text',
                left: 'center',
                top: 'center',
                z: -10,
                style: {
                    text: '百发百中',
                    fontSize: 90,
                    fontWeight: 900,
                    fill: '#e5e7eb',
                    opacity: 0.18,
                },
            }],
            tooltip: {
                trigger: 'item',
                borderRadius: 8,
                backgroundColor: 'rgba(255,255,255,0.97)',
                textStyle: { color: '#222', fontSize: 14 },
                formatter: (params) => {
                    if (params.dataType === 'node')
                        return `<b style="color:${params.color}">${params.name}</b><br/>权重: <b>${Number(params.value).toExponential(2)}</b>`
                    return `<b style="color:#b91c1c">${params.data.source}</b> → <b style="color:#b91c1c">${params.data.target}</b><br/>强度: <b>${Number(params.data.value).toExponential(2)}</b>`
                },
            },
            series: [{
                type: 'graph',
                layout: 'none',
                data: nodes,
                links,
                roam: true,
                draggable: false,
                left: 'center',
                top: 'center',
                label: {
                    show: true,
                    position: 'right',
                    fontSize: 12,
                    color: '#333',
                    formatter: '{b}',
                },
                lineStyle: {
                    curveness: 0.12,
                    opacity: 0.6,
                },
                emphasis: {
                    focus: 'adjacency',
                    lineStyle: { width: 4, color: '#b91c1c', opacity: 0.95 },
                    itemStyle: { borderColor: '#b91c1c', borderWidth: 3 },
                    label: { fontWeight: 'bold', color: '#b91c1c' },
                },
            }],
        })
        graphInstance.current.resize()
    }, [])

    /* ── Pie chart for stats ── */
    const renderPie = useCallback((data) => {
        if (!pieRef.current || !data?.pos_distribution) return
        if (pieInstance.current) {
            pieInstance.current.dispose()
            pieInstance.current = null
        }
        pieInstance.current = echarts.init(pieRef.current)
        const parsePosLabel = (key) => {
            const parts = key.split('_')
            return parts.length > 1 ? parts.slice(1).join('_') : key
        }
        const pieData = Object.entries(data.pos_distribution).map(([key, val], i) => ({
            name: parsePosLabel(key),
            value: val.count,
            ratio: val.ratio,
            itemStyle: { color: COLORS[i % COLORS.length] },
        }))
        pieInstance.current.setOption({
            tooltip: {
                trigger: 'item',
                formatter: (p) => `${p.name}：${(p.data.ratio * 100).toFixed(2)}%（${p.data.value}词）`,
            },
            series: [{
                type: 'pie',
                radius: ['38%', '78%'],
                center: ['50%', '50%'],
                avoidLabelOverlap: false,
                data: pieData,
                label: {
                    show: true,
                    position: 'inside',
                    fontSize: 11,
                    color: '#fff',
                    textBorderColor: 'rgba(0,0,0,0.35)',
                    textBorderWidth: 2,
                    formatter: (p) => `${p.name}\n${(p.data.ratio * 100).toFixed(2)}%`,
                },
                labelLine: { show: false },
                emphasis: {
                    itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.2)' },
                },
            }],
        })
    }, [])

    /* ── ECharts word cloud for keyword card ── */
    const renderKwWordCloud = useCallback((segRows) => {
        if (!kwCloudRef.current) return
        if (kwCloudInstance.current) {
            kwCloudInstance.current.dispose()
            kwCloudInstance.current = null
        }
        kwCloudInstance.current = echarts.init(kwCloudRef.current)
        const data = segRows
            .filter(row => row[0] && Number(row[1]) > 0)
            .map(row => ({ name: String(row[0]), value: Number(row[1]) }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 400)
        kwCloudInstance.current.setOption({
            tooltip: { show: true, formatter: (p) => `${p.name}：${p.value} 次` },
            series: [{
                type: 'wordCloud',
                shape: 'rectangle',
                left: 'center',
                top: 'center',
                width: '95%',
                height: '95%',
                sizeRange: [12, 56],
                rotationRange: [-45, 45],
                rotationStep: 45,
                gridSize: 8,
                drawOutOfBound: false,
                data,
                textStyle: {
                    fontFamily: 'sans-serif',
                    fontWeight: 'bold',
                    color: () => COLORS[Math.floor(Math.random() * COLORS.length)],
                },
                emphasis: {
                    focus: 'self',
                    textStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.25)' },
                },
            }],
        })
    }, [])

    useEffect(() => {
        const onResize = () => {
            graphInstance.current?.resize()
            pieInstance.current?.resize()
            kwCloudInstance.current?.resize()
        }
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])

    useEffect(() => {
        if (networkData) {
            setTimeout(() => renderGraph(networkData), 60)
        }
    }, [networkData, renderGraph])

    useEffect(() => {
        if (statsData) {
            setTimeout(() => renderPie(statsData), 60)
        }
    }, [statsData, renderPie])

    useEffect(() => {
        if (segmentResults.length > 0) {
            setTimeout(() => renderKwWordCloud(segmentResults), 60)
        }
    }, [segmentResults, renderKwWordCloud])

    /* ── Main analysis flow ── */
    const doAnalyze = async () => {
        if (inputMode === 'file' && fileList.length === 0)
            return messageApi.warning('请先选择要分析的文件')
        if (inputMode === 'text' && !textContent.trim())
            return messageApi.warning('请输入要分析的文本内容')

        setAnalyzing(true)
        setPhase('analyzing')
        setKeywords([])
        setSegmentResults([])
        setNetworkData(null)
        setStatsData(null)
        setProgress({ current: 0, total: 0, message: '正在获取关键词...' })

        fileRef.current = inputMode === 'file' ? fileList[0] : null
        textRef.current = inputMode === 'text' ? textContent : ''

        try {
            // Step 1: fetch keywords first
            let keywordData = []
            if (inputMode === 'file' && fileRef.current) {
                keywordData = await fetchKeywordsFile(fileRef.current, topN)
            } else {
                keywordData = await fetchKeywordsText(textRef.current, topN)
            }

            const kw = (Array.isArray(keywordData) ? keywordData : [])
                .map((item) => ({
                    word: String(item?.word ?? ''),
                    score: Number(item?.score) || 0,
                }))
                .filter(item => item.word)

            setKeywords(kw)

            const vocabulary = kw.map(k => k.word)
            setProgress({ current: 0, total: 0, message: '关键词获取完成，提交分词分析任务...' })

            // Step 2: submit analyze task with vocabulary
            let taskData
            if (inputMode === 'file') {
                taskData = await analyzeFile(fileList[0], vocabulary)
            } else {
                taskData = await analyzeText(textContent, vocabulary)
            }
            const { task_id } = taskData
            setProgress(p => ({ ...p, message: '任务已创建，开始分词分析...' }))

            // Step 3: subscribe to analysis stream (gets stats + word result)
            await subscribeStream(
                task_id,
                (prog) => setProgress({ current: prog.current, total: prog.total, message: prog.message }),
                async (result) => {
                    const segRows = Array.isArray(result) ? result : []
                    setSegmentResults(segRows)

                    // Step 4: build network
                    setPhase('network')
                    setProgress({ current: 0, total: 0, message: '正在提交网络图分析任务...' })
                    try {
                        const netTask = await analyzeNetwork(fileRef.current, textRef.current, kw, topN)
                        const { task_id: netTaskId } = netTask
                        setProgress(p => ({ ...p, message: '网络图分析中...' }))
                        await subscribeStream(
                            netTaskId,
                            (prog) => setProgress({ current: prog.current, total: prog.total, message: prog.message }),
                            (netResult) => {
                                setNetworkData(netResult)
                                setAnalyzing(false)
                                setPhase(null)
                                messageApi.success('关系网络图生成完成')
                            },
                            (err) => {
                                setAnalyzing(false)
                                setPhase(null)
                                if (!err?.handled) messageApi.error(err.message || '网络图分析失败')
                            },
                        )
                    } catch (e) {
                        setAnalyzing(false)
                        setPhase(null)
                        if (!e?.handled) messageApi.error(e.message || '网络图请求失败')
                    }
                },
                (err) => {
                    setAnalyzing(false)
                    setPhase(null)
                    if (!err?.handled) messageApi.error(err.message || '分词分析失败')
                },
                (stats) => setStatsData(stats),
            )
        } catch (err) {
            setAnalyzing(false)
            setPhase(null)
            if (!err?.handled) messageApi.error(err.message || '请求失败，请稍后重试')
        }
    }

    const handleAnalyze = async () => {
        if (!isLoggedIn) {
            setLoginModalOpen(true)
            return
        }
        doAnalyze()
    }

    const uploadProps = {
        name: 'file', multiple: false,
        accept: '.docx,.txt',
        maxCount: 1, fileList, showUploadList: false,
        beforeUpload: (file) => {
            const ext = file.name.split('.').pop().toLowerCase()
            const allowed = ['docx', 'txt']
            if (!allowed.includes(ext)) {
                messageApi.error(`仅支持 ${allowed.map(e => e.toUpperCase()).join(' / ')} 格式`)
                return Upload.LIST_IGNORE
            }
            if (file.size > 10 * 1024 * 1024) {
                messageApi.error('文件大小不能超过 10MB')
                return Upload.LIST_IGNORE
            }
            setFileList([file])
            return false
        },
        onRemove: () => setFileList([]),
    }

    const countMap = useMemo(() => {
        const m = {}
        segmentResults.forEach(row => {
            if (row[0]) m[String(row[0])] = Number(row[1]) || 0
        })
        return m
    }, [segmentResults])

    const kwColumns = [
        {
            title: '排名', key: 'rank', width: 60,
            render: (_, __, idx) => (
                <Tag color={idx < 3 ? ['gold', 'silver', '#cd7f32'][idx] : 'default'}>{idx + 1}</Tag>
            ),
        },
        {
            title: '关键词', dataIndex: 'word', key: 'word',
            render: w => <b>{w}</b>,
        },
        {
            title: '权重得分', dataIndex: 'score', key: 'score',
            render: (score) => (
                <span style={{ color: '#555', fontSize: 12 }}>{Number(score).toExponential(2)}</span>
            ),
        },
        {
            title: '出现频次', key: 'count',
            render: (_, row) => (
                <b>{countMap[row.word] ?? '-'}</b>
            ),
        },
    ]

    const progressPercent = progress.total > 0
        ? Math.round((progress.current / progress.total) * 100)
        : 0

    const segmentColumns = [
        {
            title: '词语', dataIndex: 0, key: 'word', width: '60%',
            render: (word) => <span>{word || '-'}</span>,
        },
        {
            title: '出现次数', dataIndex: 1, key: 'count', width: '40%',
            render: (count) => <b>{Number(count) || 0}</b>,
        },
    ]

    const downloadSegmentResult = () => {
        if (!segmentResults.length) {
            messageApi.warning('暂无分词结果可下载')
            return
        }
        const sheetData = [
            ['词语', '出现次数'],
            ...segmentResults.map((item) => [String(item?.[0] ?? ''), Number(item?.[1]) || 0]),
        ]
        const workbook = XLSX.utils.book_new()
        const worksheet = XLSX.utils.aoa_to_sheet(sheetData)
        XLSX.utils.book_append_sheet(workbook, worksheet, '分词结果')
        XLSX.writeFile(workbook, '分词结果.xlsx')
    }

    const downloadKwCloudImage = () => {
        if (!kwCloudInstance.current) return messageApi.warning('词云图尚未生成')
        const url = kwCloudInstance.current.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' })
        const a = document.createElement('a')
        a.href = url
        a.download = '词云图.png'
        a.click()
    }

    const downloadGraphImage = () => {
        if (!graphInstance.current) return messageApi.warning('关系网络图尚未生成')
        const url = graphInstance.current.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fafbff' })
        const a = document.createElement('a')
        a.href = url
        a.download = '关系网络图.png'
        a.click()
    }

    const downloadPosRatioResult = () => {
        const posDistribution = statsData?.pos_distribution
        if (!posDistribution || !Object.keys(posDistribution).length) {
            messageApi.warning('暂无词性占比可下载')
            return
        }

        const parsePos = (rawKey) => {
            const key = String(rawKey || '')
            const parts = key.split('_')
            if (parts.length > 1) {
                return {
                    code: parts[0],
                    name: parts.slice(1).join('_'),
                }
            }
            return { code: key, name: key }
        }

        const sheetData = [
            ['词性', '词性代码', '数量', '占比'],
            ...Object.entries(posDistribution).map(([key, val]) => {
                const parsed = parsePos(key)
                return [
                    parsed.name,
                    parsed.code,
                    Number(val?.count) || 0,
                    Number(val?.ratio) || 0,
                ]
            }),
        ]

        const workbook = XLSX.utils.book_new()
        const worksheet = XLSX.utils.aoa_to_sheet(sheetData)
        XLSX.utils.book_append_sheet(workbook, worksheet, '词性占比')
        XLSX.writeFile(workbook, '词性占比.xlsx')
    }

    return (
        <div className={styles.pageWrapper}>
            {contextHolder}
            <LoginModal
                open={loginModalOpen}
                onCancel={() => setLoginModalOpen(false)}
                continueAction={doAnalyze}
            />
            <WordcloudVipModal
                open={vipModalOpen}
                onClose={() => setVipModalOpen(false)}
                onSuccess={handleVipSuccess}
            />
            <div className={styles.layout}>

                {/* ── 左侧面板 30% ── */}
                <div className={styles.leftPanel}>

                    {/* 输入内容 */}
                    <div className={styles.sectionCard}>
                        <div className={styles.sectionHeader}>
                            <FileTextOutlined className={styles.sectionIcon} />
                            <span>输入内容</span>
                        </div>
                        <Tabs activeKey={inputMode} onChange={setInputMode} size="small" items={[
                            {
                                key: 'file',
                                label: <span><CloudUploadOutlined /> 上传文件</span>,
                                children: (
                                    <Dragger {...uploadProps} disabled={analyzing} className={styles.uploadDragger}>
                                        {fileList.length > 0 ? (
                                            <div className={styles.fileDisplay}>
                                                <CloudUploadOutlined style={{ fontSize: 22, color: '#1677ff', flexShrink: 0 }} />
                                                <span className={styles.fileName}>{fileList[0].name}</span>
                                                <CloseCircleOutlined
                                                    className={styles.fileRemove}
                                                    onClick={(e) => { e.stopPropagation(); setFileList([]) }}
                                                    title="移除文件"
                                                />
                                            </div>
                                        ) : (
                                            <div className={styles.uploadPrompt}>
                                                <InboxOutlined style={{ color: '#1677ff', fontSize: 28 }} />
                                                <p className={styles.uploadText}>点击或拖拽文件上传</p>
                                                    <p className={styles.uploadHint}>DOCX / TXT，不超过 10MB</p>
                                            </div>
                                        )}
                                    </Dragger>
                                ),
                            },
                            {
                                key: 'text',
                                label: <span><FileTextOutlined /> 输入文本</span>,
                                children: (
                                    <TextArea
                                        className={styles.textArea}
                                        rows={4}
                                        placeholder="粘贴或输入文本..."
                                        value={textContent}
                                        onChange={(e) => setTextContent(e.target.value)}
                                        disabled={analyzing}
                                        showCount
                                        maxLength={200000}
                                    />
                                ),
                            },
                        ]} />
                    </div>

                    {/* 参数设置 */}
                    <div className={styles.sectionCard}>
                        <div className={styles.sectionHeader}>
                            <FilterOutlined className={styles.sectionIcon} />
                            <span>参数设置</span>
                        </div>
                        <div className={styles.settingsItems}>
                            <div className={styles.settingItem}>
                                <span className={styles.settingItemLabel}>关键词数</span>
                                <InputNumber
                                    size="small"
                                    min={5}
                                    max={200}
                                    value={topN}
                                    onChange={(v) => setTopN(v || 50)}
                                    style={{ width: 110 }}
                                    addonAfter="个词"
                                />
                            </div>
                        </div>
                    </div>

                    <Button
                        type="primary"
                        size="large"
                        className={styles.analyzeBtn}
                        onClick={handleAnalyze}
                        loading={analyzing}
                        icon={<ApartmentOutlined />}
                        disabled={analyzing}
                    >
                        {analyzing ? '分析中...' : '开始分析'}
                    </Button>
                </div>

                {/* ── 右侧面板 70% ── */}
                <div className={styles.rightPanel}>
                    <div className={styles.rightInner}>

                    {/* Stats panel — shown as soon as stats arrive */}
                    {statsData && (
                        <div className={`${styles.sectionCard} ${styles.statsCard}`} style={{ padding: '0 16px 14px' }}>
                            <div className={styles.statsRow}>
                                <div className={styles.statsTableSide} style={{ paddingTop: 14, display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ border: '1px solid #edf0f8', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                                        <div className={styles.sectionHeader} style={{ margin: 0, padding: '8px 12px 7px', borderRadius: 0, flexShrink: 0 }}>
                                            <PieChartOutlined className={styles.sectionIcon} />
                                            <span>分词统计</span>
                                        </div>
                                        <table className={styles.statsTable} style={{ borderRadius: 0, border: 'none', borderTop: '1px solid #e8eaf0' }}>
                                            <tbody>
                                                <tr>
                                                    <td className={styles.statsLabel}>总字数</td>
                                                    <td className={styles.statsValue}>{statsData.total_char_count?.toLocaleString()}</td>
                                                </tr>
                                                <tr>
                                                    <td className={styles.statsLabel}>总词数</td>
                                                    <td className={styles.statsValue}>{statsData.total_word_count?.toLocaleString()}</td>
                                                </tr>
                                                <tr>
                                                    <td className={styles.statsLabel}>特征词数</td>
                                                    <td className={styles.statsValue}>{statsData.feature_word_count?.toLocaleString()}</td>
                                                </tr>
                                                <tr>
                                                    <td className={styles.statsLabel}>关键词数</td>
                                                    <td className={styles.statsValue}>{keywords.length}</td>
                                                </tr>
                                                <tr>
                                                    <td className={styles.statsLabel}>下载</td>
                                                    <td className={styles.statsValue}>
                                                        <span
                                                            className={styles.statsDownloadLink}
                                                            onClick={downloadSegmentResult}
                                                        >
                                                            分词结果.xlsx
                                                        </span>
                                                        <span
                                                            className={`${styles.statsDownloadLink} ${styles.statsDownloadLinkSecondary}`}
                                                            onClick={downloadPosRatioResult}
                                                        >
                                                            词性占比.xlsx
                                                        </span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className={styles.statsPieSide} ref={pieRef} />
                                <div className={styles.statsResultSide} style={{ paddingTop: 14 }}>
                                    <div className={styles.statsResultTableWrap}>
                                        <div className={styles.sectionHeader} style={{ margin: 0, padding: '8px 12px 7px', borderRadius: 0, flexShrink: 0 }}>
                                            <FileTextOutlined className={styles.sectionIcon} />
                                            <span>全文分词结果</span>
                                            <Badge count={segmentResults.length} style={{ backgroundColor: '#1677ff', marginLeft: 6 }} overflowCount={99999} />
                                        </div>
                                        <Table
                                            dataSource={segmentResults}
                                            columns={segmentColumns}
                                            rowKey={(_, idx) => idx}
                                            size="small"
                                            pagination={false}
                                            scroll={{ y: 'calc(100% - 78px)' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Empty state */}
                    {!keywords.length && !analyzing && !statsData && (
                        <div className={styles.emptyState}>
                            <ApartmentOutlined className={styles.emptyIcon} />
                            <p className={styles.emptyText}>
                                上传文件或输入文本，点击「开始分析」<br />
                                提取关键词并生成词语关系网络图
                            </p>
                        </div>
                    )}

                    {/* Analyzing-phase centered progress */}
                    {analyzing && phase === 'analyzing' && (
                        <div className={styles.progressCenter}>
                            <div className={styles.progressBox}>
                                <div className={styles.progressTitle}>分词分析</div>
                                <Progress
                                    type="circle"
                                    percent={progressPercent}
                                    size={80}
                                    strokeWidth={7}
                                    strokeColor={{ '0%': '#1a73e8', '100%': '#7c3aed' }}
                                    trailColor="#eef3ff"
                                    format={(p) => (
                                        <span style={{ fontSize: 14, fontWeight: 700, color: '#1a73e8' }}>{p}%</span>
                                    )}
                                />
                                <p className={styles.progressMessage}>{progress.message}</p>
                            </div>
                        </div>
                    )}

                    {/* Keywords card — 7:3 layout: wordcloud left, table right */}
                    {keywords.length > 0 && (
                        <div className={`${styles.sectionCard} ${styles.kwCard}`} style={{ padding: 0, overflow: 'hidden' }}>
                            <div style={{ display: 'flex', gap: 0, height: 400 }}>
                                {/* Left 70%: ECharts word cloud from analyze results, fills full height */}
                                <div style={{ flex: 7, minWidth: 0, height: '100%', position: 'relative' }}>
                                    <div ref={kwCloudRef} style={{ width: '100%', height: '100%' }} />
                                    <Button
                                        type="primary"
                                        size="small"
                                        icon={<DownloadOutlined />}
                                        onClick={downloadKwCloudImage}
                                        style={{
                                            position: 'absolute', top: 8, left: 8,
                                            fontSize: 11, padding: '0 8px', height: 24,
                                            opacity: 0.85,
                                        }}
                                        title="下载词云图"
                                    />
                                </div>
                                {/* Right 30%: Keyword table with its own header */}
                                <div style={{ flex: 3, minWidth: 0, height: '100%', overflow: 'hidden', borderLeft: '1px solid #eaecf5', display: 'flex', flexDirection: 'column' }}>
                                    <div className={styles.sectionHeader} style={{ margin: '0', padding: '10px 14px 9px', flexShrink: 0 }}>
                                        <KeyOutlined className={styles.sectionIcon} />
                                        <span>关键词列表</span>
                                        <Badge count={keywords.length} style={{ backgroundColor: '#1677ff', marginLeft: 6 }} />
                                    </div>
                                    <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                        <Table
                                            dataSource={keywords}
                                            columns={kwColumns}
                                            rowKey="word"
                                            size="small"
                                            pagination={false}
                                            scroll={{ y: 'calc(400px - 44px)' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Network-phase progress strip */}
                    {analyzing && phase === 'network' && (
                        <div className={styles.netProgressStrip}>
                            <span className={styles.netProgressLabel}>构建关系网络</span>
                            <div style={{ flex: 1 }}>
                                <Progress
                                    percent={progressPercent}
                                    size="small"
                                    strokeColor={{ '0%': '#1a73e8', '100%': '#7c3aed' }}
                                    trailColor="#eef3ff"
                                />
                            </div>
                            <span className={styles.netProgressMsg}>{progress.message}</span>
                        </div>
                    )}

                    {/* Force-directed network graph */}
                    {networkData && (
                        <div className={`${styles.sectionCard} ${styles.graphCard}`}>
                            <div className={styles.sectionHeader}>
                                <ApartmentOutlined className={styles.sectionIcon} />
                                <span>关系网络图</span>
                            </div>
                            <div style={{ position: 'relative', width: '70%', aspectRatio: '1 / 1' }}>
                                <div ref={graphRef} style={{ width: '100%', height: '100%' }} />
                                <span style={{
                                    position: 'absolute', top: 8, right: 8,
                                    fontSize: 11, color: '#bbb', fontWeight: 400,
                                    pointerEvents: 'none',
                                }}>
                                    可拖拽节点 · 滚轮缩放
                                </span>
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<DownloadOutlined />}
                                    onClick={downloadGraphImage}
                                    style={{
                                        position: 'absolute', top: 8, left: 8,
                                        fontSize: 11, padding: '0 8px', height: 24,
                                        opacity: 0.85,
                                    }}
                                    title="下载关系网络图"
                                />
                            </div>
                        </div>
                    )}

                    </div>
                </div>
            </div>
        </div>
    )
}

export default ChineseSegPage
